import React from 'react'
import {
  WorkerCounterTemplate,
  CreateWorkerCounterTemplateParams,
} from '../../api/worker-counter-templates'
import { Field, Form, Formik, FieldArray } from 'formik'
import InputField from '../form/fields/input'
import SelectField from '../form/fields/select'
import MultiSelectField from '../form/fields/multi-select'
import TextareaField from '../form/fields/textarea'
import {
  useCreateWorkerCounterTemplateMutation,
  useUpdateWorkerCounterTemplateMutation,
} from '../../queries/worker-counter-templates'
import { RequestButton } from '../request-components/request-components'
import { Columns, Heading, Box, Button, Icon } from 'react-bulma-components'
import { useNavigate, useLocation } from 'react-router-dom'
import { useEmployerRulesQuery } from '../../queries/employer-rules'
import { EmployerRule } from '../../api/employer-rules'
import { RemoveIcon } from '../icons'
import { dateToDateInputValue } from '../../utils/date'
import CheckboxField from '../form/fields/checkbox'

interface WorkerCounterTemplatesFormProps {
  workerCounterTemplate?: WorkerCounterTemplate
  employerId: string
}

type FormValues = Partial<CreateWorkerCounterTemplateParams> & {
  usagePeriods: Array<{
    startDate: string
    endDate?: string
  }>
}

/**
 * Formulaire pour créer ou modifier un modèle de compteur de travailleur
 * Permet de configurer un modèle réutilisable pour les compteurs
 */
const WorkerCounterTemplatesForm: React.FC<WorkerCounterTemplatesFormProps> = ({
  workerCounterTemplate,
  employerId,
}) => {
  const createMutation = useCreateWorkerCounterTemplateMutation()
  const updateMutation = useUpdateWorkerCounterTemplateMutation()
  const navigate = useNavigate()
  const location = useLocation()

  // Determine base path from current location
  const basePath = location.pathname.includes('/employers/')
    ? `/employers/${employerId}`
    : '/settings'

  // Récupérer les règles de l'employeur pour la sélection
  const { data: employerRules } = useEmployerRulesQuery(
    { employer: employerId },
    { enabled: Boolean(employerId) },
  )

  React.useEffect(() => {
    if (createMutation.isSuccess) {
      navigate(`${basePath}/worker-counters/${createMutation.data._id}`)
    }
  }, [createMutation.isSuccess, navigate, employerId, basePath])

  if (!employerRules?.[0]) return null

  return (
    <Formik<FormValues>
      initialValues={
        workerCounterTemplate
          ? {
              name: workerCounterTemplate.name,
              description: workerCounterTemplate.description || '',
              employer: employerId,
              employerRules: workerCounterTemplate.employerRules.map(rule =>
                typeof rule === 'string' ? rule : rule._id,
              ) as string[],
              aggregationMethod: workerCounterTemplate.aggregationMethod,
              affectsPeriodSummaryAccounted: workerCounterTemplate.affectsPeriodSummaryAccounted,
              usagePeriods:
                workerCounterTemplate.usagePeriods?.map(period => ({
                  startDate: dateToDateInputValue(new Date(period.startDate)),
                  endDate: period.endDate
                    ? dateToDateInputValue(new Date(period.endDate))
                    : undefined,
                })) || [],
            }
          : {
              name: '',
              description: '',
              employer: employerId,
              employerRules: [] as string[],
              aggregationMethod: 'sum' as const,
              affectsPeriodSummaryAccounted: false,
              usagePeriods: [
                {
                  startDate: dateToDateInputValue(new Date()),
                  endDate: undefined,
                },
              ],
            }
      }
      enableReinitialize
      validate={values => {
        const errors: Record<string, any> = {}

        if (!values.name?.trim()) {
          errors.name = 'Le nom est requis'
        }

        if (!values.employerRules?.length) {
          errors.employerRules = 'Au moins une règle est requise'
        }

        // Usage periods validation
        if (!values.usagePeriods) {
          values.usagePeriods = []
        }

        // Sort periods by start date to make validation easier
        const sortedPeriods = [...values.usagePeriods].sort(
          (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
        )

        // Check overlaps and other validations
        sortedPeriods.forEach((period, index) => {
          const currentIndex = (values.usagePeriods || []).indexOf(period)

          if (!period.startDate) {
            errors[`usagePeriods.${currentIndex}.startDate`] = 'Date de début requise'
          }

          // Check if end date is after start date
          if (
            period.endDate &&
            period.startDate &&
            new Date(period.endDate) < new Date(period.startDate)
          ) {
            errors[`usagePeriods.${currentIndex}.endDate`] =
              'La date de fin doit être après la date de début'
          }

          // Check for overlaps with subsequent periods
          for (let j = index + 1; j < sortedPeriods.length; j++) {
            const nextPeriod = sortedPeriods[j]
            if (!nextPeriod) continue
            const currentEndDate = period.endDate ? new Date(period.endDate) : null
            const nextStartDate = new Date(nextPeriod.startDate)

            // If current period has no end date, it automatically overlaps with all subsequent periods
            if (!currentEndDate) {
              errors[`usagePeriods.${currentIndex}.endDate`] =
                'Cette période chevauche les périodes suivantes'
              break
            }

            // If current period's end date is after or equal to next period's start date
            if (currentEndDate >= nextStartDate) {
              errors[`usagePeriods.${currentIndex}.endDate`] =
                'Cette période chevauche la période suivante'
              break
            }
          }
        })

        return errors
      }}
      onSubmit={values => {
        const payload: CreateWorkerCounterTemplateParams = {
          ...values,
          name: values.name!.trim(),
          description: values.description?.trim() || undefined,
          employerRules: values.employerRules || [],
          employer: employerId,
          aggregationMethod: values.aggregationMethod || 'sum',
          usagePeriods:
            values.usagePeriods?.map(period => ({
              startDate: new Date(period.startDate),
              endDate: period.endDate ? new Date(period.endDate) : undefined,
            })) || [],
        }

        if (workerCounterTemplate) {
          updateMutation.mutate({
            id: workerCounterTemplate._id,
            ...payload,
          })
        } else {
          createMutation.mutate(payload)
        }
      }}
    >
      {({ values, setFieldValue }) => (
        <Form autoComplete="off">
          <Field
            label="Nom du compteur"
            name="name"
            component={InputField}
            required
            disabled={Boolean(workerCounterTemplate)}
            help="Nom unique pour identifier ce compteur"
          />

          <Field
            label="Description"
            name="description"
            component={TextareaField}
            minRows={2}
            maxRows={4}
            help="Description optionnelle du compteur"
          />

          <Heading size={5} mt={5}>
            Configuration du compteur
          </Heading>

          {/*<Columns>
            <Columns.Column size={6}>
              <Field
                label="Méthode d'agrégation"
                name="aggregationMethod"
                component={SelectField}
                options={[
                  { value: 'sum', label: 'Somme - Additionne toutes les valeurs' },
                  { value: 'max', label: 'Maximum - Prend la valeur la plus élevée' },
                  { value: 'average', label: 'Moyenne - Calcule la moyenne des valeurs' },
                  { value: 'count', label: 'Comptage - Compte le nombre d\'occurrences' },
                ]}
                required
                fullwidth
                help="Méthode utilisée pour agréger les valeurs des règles sélectionnées"
              />
            </Columns.Column>
          </Columns>*/}

          <Field
            label="Règles utilisées"
            name="employerRules"
            component={MultiSelectField}
            options={
              employerRules.map((rule: EmployerRule) => ({
                value: rule._id,
                label: `${rule.name} (${rule.shortName})`,
              })) || []
            }
            required
            help="Sélectionnez les règles employeur qui seront utilisées pour calculer la valeur du compteur"
          />

          <Field
            label="Affecte les heures comptabilisées de la semaines"
            name="affectsPeriodSummaryAccounted"
            component={CheckboxField}
            help="Si activé, les heures comptabilisées de la semaines seront affectées à ce compteur"
          />

          <Heading size={5} mt={5}>
            Périodes d'utilisation
            <p className="help mb-4">
              Définissez les périodes pendant lesquelles ce compteur sera actif. Une période sans
              date de fin est considérée comme active.
            </p>
          </Heading>

          <FieldArray name="usagePeriods">
            {({ push, remove }) => (
              <div>
                {values.usagePeriods?.map((_, index) => (
                  <Box key={index} style={{ position: 'relative' }}>
                    {values.usagePeriods && values.usagePeriods.length > 1 && (
                      <Button
                        color="danger"
                        size="small"
                        onClick={() => remove(index)}
                        style={{
                          position: 'absolute',
                          top: '1rem',
                          right: '1rem',
                        }}
                      >
                        <Icon>
                          <RemoveIcon color="white" />
                        </Icon>
                      </Button>
                    )}
                    <Columns mt={2}>
                      <Columns.Column size={6}>
                        <Field
                          label="Date de début"
                          name={`usagePeriods.${index}.startDate`}
                          component={InputField}
                          type="date"
                          required
                        />
                      </Columns.Column>
                      <Columns.Column size={6}>
                        <Field
                          label="Date de fin"
                          name={`usagePeriods.${index}.endDate`}
                          component={InputField}
                          min={values.usagePeriods?.[index]?.startDate}
                          type="date"
                          help={
                            index === values.usagePeriods.length - 1
                              ? 'Optionnel pour la période active'
                              : 'Requis pour éviter les chevauchements'
                          }
                        />
                      </Columns.Column>
                    </Columns>
                  </Box>
                ))}
                <Button
                  onClick={() =>
                    push({
                      startDate: dateToDateInputValue(new Date()),
                      endDate: undefined,
                    })
                  }
                  size="small"
                >
                  Ajouter une période
                </Button>
              </div>
            )}
          </FieldArray>

          <RequestButton
            color="primary"
            type="submit"
            mutation={workerCounterTemplate ? updateMutation : createMutation}
            mt={6}
          >
            {workerCounterTemplate ? 'Mettre à jour' : 'Créer'}
          </RequestButton>
        </Form>
      )}
    </Formik>
  )
}

export default WorkerCounterTemplatesForm
